import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
);

// Degree-3 polynomial regression using least squares (Gaussian elimination)
function calculatePolynomialRegression(dataPoints) {
  if (!dataPoints || dataPoints.length < 2) {
    return null;
  }

  const degree = 3;
  const size = degree + 1;
  const n = dataPoints.length;

  // Normalize x to [0, 1] to avoid floating-point issues with large timestamps
  const xMin = dataPoints[0].x.getTime();
  const xMax = dataPoints[dataPoints.length - 1].x.getTime();
  const xRange = xMax - xMin || 1;

  const xs = dataPoints.map(p => (p.x.getTime() - xMin) / xRange);
  const ys = dataPoints.map(p => p.y);

  // Build X^T X and X^T y for normal equations
  const XtX = Array.from({ length: size }, () => new Array(size).fill(0));
  const Xty = new Array(size).fill(0);

  for (let i = 0; i < n; i++) {
    const xPow = new Array(size);
    xPow[0] = 1;
    for (let j = 1; j < size; j++) xPow[j] = xPow[j - 1] * xs[i];
    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) XtX[row][col] += xPow[row] * xPow[col];
      Xty[row] += xPow[row] * ys[i];
    }
  }

  // Gauss-Jordan elimination with partial pivoting
  const aug = XtX.map((row, i) => [...row, Xty[i]]);
  for (let col = 0; col < size; col++) {
    let maxRow = col;
    for (let row = col + 1; row < size; row++) {
      if (Math.abs(aug[row][col]) > Math.abs(aug[maxRow][col])) maxRow = row;
    }
    [aug[col], aug[maxRow]] = [aug[maxRow], aug[col]];
    if (Math.abs(aug[col][col]) < 1e-10) continue;
    for (let row = 0; row < size; row++) {
      if (row === col) continue;
      const f = aug[row][col] / aug[col][col];
      for (let k = col; k <= size; k++) aug[row][k] -= f * aug[col][k];
    }
  }
  const coeffs = aug.map((row, i) => row[size] / row[i]);

  // Sample 100 evenly-spaced points for a smooth curve
  const result = [];
  for (let i = 0; i <= 100; i++) {
    const xNorm = i / 100;
    let y = 0;
    let xPow = 1;
    for (let j = 0; j < size; j++) { y += coeffs[j] * xPow; xPow *= xNorm; }
    result.push({ x: new Date(xMin + xNorm * xRange), y });
  }
  return result;
}

function SensorChart({ sensorsData, readings, showBestFit = false, chartHeight = 320, showTitle = true }) {
  // Sensor ids whose series are toggled off via the legend (multi-sensor mode).
  const [hiddenSeries, setHiddenSeries] = useState(() => new Set());
  // Handle backward compatibility - if readings prop is provided, use single sensor mode
  if (readings) {
    const data = {
      labels: readings.map(reading => new Date(reading.recorded_at)),
      datasets: [
        {
          label: 'Temperature (°F)',
          data: readings.map(reading => reading.temperature_f),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.4,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: 'Temperature Readings Over Time' },
        tooltip: {
          callbacks: {
            title: (items) => new Date(items[0].parsed.x).toLocaleString([], {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
            }),
          },
        },
      },
      scales: {
        x: {
          type: 'time',
          time: { displayFormats: { hour: 'MMM dd, HH:mm', day: 'MMM dd' } },
          ticks: {
            callback: (value) => new Date(value).toLocaleString([], {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
            }),
          },
          title: { display: true, text: 'Time' },
        },
        y: { title: { display: true, text: 'Temperature (°F)' } },
      },
    };

    return <Line data={data} options={options} />;
  }

  // Multi-sensor mode
  const colors = [
    'rgb(75, 192, 192)',
    'rgb(255, 99, 132)',
    'rgb(54, 162, 235)',
    'rgb(255, 206, 86)',
    'rgb(153, 102, 255)',
    'rgb(255, 159, 64)',
  ];

  const datasets = [];
  // Legend is built for ALL sensors (below) so hidden ones can be toggled back on.
  const legendItems = [];

  // Wave-height sensors read in inches (small values) and belong on their own
  // right-side axis; temperature sensors stay on the left. Track which axes are
  // actually used so we only draw the ones with data.
  let hasTemp = false;
  let hasWave = false;

  Object.entries(sensorsData || {}).forEach(([sensorId, sensorInfo], index) => {
    const color = colors[index % colors.length];
    const isWave = sensorInfo.sensor_type === 'wave_height';
    const yAxisID = isWave ? 'y1' : 'y';
    const unit = isWave ? 'in' : '°F';
    const isHidden = hiddenSeries.has(sensorId);

    // Every sensor gets a legend entry (color assigned by stable index) regardless
    // of visibility; tapping it toggles the series.
    legendItems.push({ id: sensorId, label: sensorInfo.name, color, hidden: isHidden });

    // Hidden series are dropped entirely (and don't force their axis to render).
    if (isHidden) return;
    if (isWave) hasWave = true; else hasTemp = true;

    const dataPoints = sensorInfo.readings.map(reading => ({
      x: new Date(reading.recorded_at),
      y: reading.temperature_f
    }));

    // Add main data series
    datasets.push({
      label: sensorInfo.name,
      data: dataPoints,
      borderColor: color,
      backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.2)'),
      tension: 0.4,
      pointRadius: 3,
      pointHoverRadius: 5,
      yAxisID,
      unit,
    });

    // Add best fit line if enabled
    if (showBestFit && dataPoints.length >= 2) {
      const bestFitData = calculatePolynomialRegression(dataPoints);
      if (bestFitData) {
        datasets.push({
          label: `${sensorInfo.name} (Best Fit)`,
          data: bestFitData,
          borderColor: color,
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          yAxisID,
          unit,
        });
      }
    }
  });

  const data = {
    datasets,
  };

  // On a phone the full "MMM dd, HH:mm" label on every auto tick crushes the axis,
  // so narrow screens get few, compact hour ticks (like the forecast chart).
  const isNarrow = typeof window !== 'undefined' && window.innerWidth < 600;

  const options = {
    responsive: true,
    // Fill the fixed-height wrapper below rather than a width:height ratio, so the
    // plot keeps a constant height on any width.
    maintainAspectRatio: false,
    plugins: {
      // Legend is rendered as HTML above the chart (see below) so long sensor
      // names grow the card vertically instead of eating into the plot height.
      legend: { display: false },
      title: { display: showTitle, text: 'Multi-Sensor Readings Over Time' },
      tooltip: {
        callbacks: {
          title: (items) => new Date(items[0].parsed.x).toLocaleString([], {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
          }),
          label: (item) => {
            const unit = item.dataset.unit || '';
            const y = item.parsed.y;
            const value = y == null ? '' : y.toFixed(1);
            // °F attaches directly (72.5°F); other units get a space (3.2 in).
            const suffix = unit === '°F' ? '°F' : unit ? ` ${unit}` : '';
            return `${item.dataset.label}: ${value}${suffix}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: { displayFormats: { hour: 'MMM dd, HH:mm', day: 'MMM dd' } },
        ticks: {
          maxTicksLimit: isNarrow ? 6 : 12,
          maxRotation: isNarrow ? 0 : 50,
          autoSkip: true,
          font: isNarrow ? { size: 9 } : undefined,
          callback: (value) => new Date(value).toLocaleString([], isNarrow
            ? { hour: 'numeric', hour12: true }
            : { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
        },
        title: { display: !isNarrow, text: 'Time' },
      },
      y: {
        type: 'linear',
        position: 'left',
        display: hasTemp,
        title: { display: true, text: 'Temperature (°F)' },
      },
      // Wave-height axis on the right; only drawn when a wave sensor is shown, and
      // its gridlines are suppressed so they don't overlap the temperature grid.
      y1: {
        type: 'linear',
        position: 'right',
        display: hasWave,
        beginAtZero: true,
        title: { display: true, text: 'Wave Height (in)' },
        grid: { drawOnChartArea: false },
      },
    },
  };

  const toggleSeries = (id) => setHiddenSeries(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // HTML legend above the chart. It wraps and grows the container vertically; the
  // chart below keeps a fixed height so its Y-axis is never crushed, and its width
  // stays responsive (no h-scroll). Tapping a label toggles that series.
  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px 16px', marginBottom: 8 }}>
        {legendItems.map(item => (
          <span
            key={item.id}
            onClick={() => toggleSeries(item.id)}
            role="button"
            title={item.hidden ? 'Show this series' : 'Hide this series'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
              cursor: 'pointer', userSelect: 'none',
              opacity: item.hidden ? 0.4 : 1,
              textDecoration: item.hidden ? 'line-through' : 'none',
            }}
          >
            <span style={{ width: 12, height: 12, borderRadius: 2, background: item.color, display: 'inline-block' }} />
            {item.label}
          </span>
        ))}
      </div>
      <div style={{ height: chartHeight }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
}

export default SensorChart;